import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { CONTENT_WIDTH } from "../../constants/layout";
import { useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Tab,
    TabList,
    TabPanel,
    Tabs,
    Autocomplete,
    useTheme,
    Snackbar,
    Modal,
    ModalDialog,
    ModalClose,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Button
} from '@mui/joy';
import axios from 'axios';
import CustomInput from "../UI/custom_elements/customInput";
import PrimaryButton from "../UI/custom_elements/primaryButton";
import SecondaryButton from "../UI/custom_elements/secondaryButton";
import SessionContext from "../../store";

const ManualCrixto = () => {
    const theme = useTheme();
    const location = useLocation();
    const [balanceLoaded, setBalanceLoaded] = useState(false);
    const [platformFee, setPlatformFee] = useState(null);
    const [loadingFee, setLoadingFee] = useState(true);
    const { session } = useContext(SessionContext);

    // Retry mode: viene desde /transactions con datos pre-llenados
    const retryTransaction = location.state?.retryMode ? location.state.transaction : null;

    const banksFetchedRef = useRef(false);

    const [availableBalance, setAvailableBalance] = useState('0.0000');

    const [activeTab, setActiveTab] = useState(0); // 0 = Operación (stepper) · 1 = Recurrentes
    // Vertical stepper inside Operación: 0 Cotización · 1 Método de pago · 2 Información del Destinatario
    const [activeStep, setActiveStep] = useState(0);
    const stepRefs = [useRef(null), useRef(null), useRef(null)];
    // Recurrentes (clientes con transacciones exitosas) + prefill al "Reusar"
    const [recurrentes, setRecurrentes] = useState([]);
    const [recPage, setRecPage] = useState(1);
    const [loadingRecurrentes, setLoadingRecurrentes] = useState(false);
    const recurrentesFetchedRef = useRef(false);
    const prefillFlowRef = useRef(null);
    const [montoUSDT, setMontoUSDT] = useState('');
    const [montoBs, setMontoBs] = useState('');
    const [activeInput, setActiveInput] = useState('USDT');
    const [externalId, setExternalId] = useState('');
    const [isRetryMode, setIsRetryMode] = useState(false);

    const [previewData, setPreviewData] = useState(null);
    const [quoteData, setQuoteData] = useState(null);
    const [isQuoteConfirmed, setIsQuoteConfirmed] = useState(false);
    const [insufficientFunds, setInsufficientFunds] = useState(false);

    const [montoPagar, setMontoPagar] = useState('0,000000');
    const [montoRecibir, setMontoRecibir] = useState('0,00');

    const [phoneDestino, setPhoneDestino] = useState('');
    const [idNumberDestino, setIdNumberDestino] = useState('');
    const [selectedBank, setSelectedBank] = useState('');
    const [banks, setBanks] = useState([]);

    const [loadingConfirm, setLoadingConfirm] = useState(false);
    const [loadingTransfer, setLoadingTransfer] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');


    const [discrepancyDialogOpen, setDiscrepancyDialogOpen] = useState(false);
    const [discrepancyData, setDiscrepancyData] = useState(null);
    const [pendingQuoteData, setPendingQuoteData] = useState(null);

    // Selección de flujo de pago (Pago Móvil / Transferencia)
    const [selectedFlow, setSelectedFlow] = useState(null); // 'mobile' o 'transfer'
    // Cambio de método con confirmación (borra los datos del destinatario ya cargados)
    const [methodChangeOpen, setMethodChangeOpen] = useState(false);
    const [pendingMethod, setPendingMethod] = useState(null);
    // Confirmación al reiniciar la operación desde el paso 1 (Cotización)
    const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);

    // Campos para transferencia bancaria
    const [accountNumber, setAccountNumber] = useState('');
    const [idNumberDestinoTransfer, setIdNumberDestinoTransfer] = useState('');
    const [beneficiaryName, setBeneficiaryName] = useState('');

    // Retry mode: campos de cuenta bancaria
    const [retryAccountNumber, setRetryAccountNumber] = useState('');
    const [retryIdNumber, setRetryIdNumber] = useState('');
    const [retryBeneficiaryName, setRetryBeneficiaryName] = useState('');
    const [loadingRetry, setLoadingRetry] = useState(false);

    // Timer states
    const [timeRemaining, setTimeRemaining] = useState(120); // 2 minutos en segundos
    const timerIntervalRef = useRef(null);

    const BANK_UNAVAILABLE_MSG = 'El banco no está disponible para completar la transacción';

    // Traduce los errores del backend (códigos en inglés) a mensajes en español para el usuario.
    // En prod, los errores de validador llegan como texto plano genérico, por eso la validación
    // de teléfono/identificación se hace en el cliente antes de enviar.
    const CASHOUT_ERROR_ES = {
        PAGO_MOVIL_UNAVAILABLE: 'PagoMóvil no está disponible en este momento. Intenta más tarde.',
        TRANSFERENCIA_DIRECTA_UNAVAILABLE: 'La transferencia directa no está disponible en este momento. Intenta más tarde.',
        INVALID_ID: 'Identificador de cotización inválido.',
        QUOTE_EXPIRED: 'La cotización expiró. Inicia una nueva cotización.',
        INVALID_BANK_CODE: 'El banco seleccionado no es válido.',
        INSUFFICIENT_FUNDS: 'Saldo insuficiente para completar la transferencia.',
    };
    const translateCashoutError = (error, fallback = 'No se pudo completar la transferencia. Intenta de nuevo.') => {
        const data = error?.response?.data;
        const raw = (data && (data.message || data.errorMessage)) || (typeof data === 'string' ? data : '') || error?.message || '';
        if (CASHOUT_ERROR_ES[raw]) return CASHOUT_ERROR_ES[raw];
        const r = raw.toLowerCase();
        if (r.includes('phone number length')) return 'El teléfono debe tener 10 u 11 dígitos.';
        if (r.includes('phone must start') || r.includes('prefij')) return 'El teléfono debe comenzar con un prefijo válido (0414, 0416, 0412, 0422, 0424, 0426).';
        if (r.includes('accountnumber')) return 'El número de cuenta debe tener 20 dígitos.';
        if (r.includes('idnumberdestino')) return 'La identificación debe iniciar con V, J o E seguida de números.';
        if (r.includes('phonedestino')) return 'El teléfono del destinatario es obligatorio.';
        if (r.includes('bankaccount')) return 'Debes seleccionar el banco del destinatario.';
        if (r.includes('beneficiaryname')) return 'El nombre del beneficiario es obligatorio.';
        if (r.includes('insufficient')) return 'Saldo insuficiente para completar la transferencia.';
        if (r.includes('cannot be processed')) return 'No se pudo procesar la solicitud en este momento. Intenta de nuevo más tarde.';
        return fallback;
    };

    // Timer effect - inicia cuando se pasa al step 2
    useEffect(() => {
        if (activeStep >= 1 && isQuoteConfirmed) {
            // Iniciar el timer
            setTimeRemaining(120);
            
            timerIntervalRef.current = setInterval(() => {
                setTimeRemaining((prev) => {
                    if (prev <= 1) {
                        // Tiempo agotado
                        clearInterval(timerIntervalRef.current);
                        handleTimeExpired();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => {
                if (timerIntervalRef.current) {
                    clearInterval(timerIntervalRef.current);
                }
            };
        } else {
            // Limpiar timer si no estamos en tab 1
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep, isQuoteConfirmed]);

    // Autoscroll the active step into view when it changes.
    useEffect(() => {
        const el = stepRefs[activeStep] && stepRefs[activeStep].current;
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep]);

    const handleTimeExpired = () => {
        setErrorMessage('El tiempo para completar la operación ha expirado. Por favor, inicie una nueva cotización.');
        resetForm();
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (banksFetchedRef.current) return;

        const fetchBanks = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v2/banks`,
                    {
                        headers: {
                            'Authorization': `Bearer ${session.token}`
                        }
                    }
                );
                if (response.data && response.data.data) {
                    setBanks(response.data.data);
                } else {
                    setBanks([]);
                }
            } catch (error) {
                if (error.response && error.response.status === 403) {
                    setErrorMessage(BANK_UNAVAILABLE_MSG);
                } else {
                    setErrorMessage('Error al obtener la lista de bancos');
                }
                setBanks([]);
            }
        };

        const fetchBalance = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v2/balance`,
                    {
                        headers: {
                            'Authorization': `Bearer ${session.token}`
                        }
                    }
                );
                if (response.data && response.data.data) {
                    setAvailableBalance(truncateToDecimals(parseFloat(response.data.data.available), 6));
                }
            } catch (error) {
                if (error.response && error.response.status === 403) {
                    setErrorMessage(BANK_UNAVAILABLE_MSG);
                } else {
                    setErrorMessage('Error al obtener el saldo disponible');
                }
            } finally {
                setBalanceLoaded(true);
            }
        };

        const fetchInitialFee = async () => {
            setLoadingFee(true);
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v2/preview`,
                    {
                        amount: 1,
                        currency: 'USDT'
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${session.token}`
                        }
                    }
                );
                if (response.data && response.data.ves_amount !== undefined) {
                    setPlatformFee(response.data.ves_amount);
                }
            } catch (error) {
                console.error('Error al obtener la tarifa inicial');
            } finally {
                setLoadingFee(false);
            }
        };

        fetchBanks();
        fetchBalance();
        fetchInitialFee();
        banksFetchedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Activar retry mode si viene state desde /transactions
    useEffect(() => {
        if (retryTransaction) {
            const info = retryTransaction.receiver_info || {};
            setExternalId(retryTransaction.external_id || '');
            setRetryAccountNumber(info.accountNumber || '');
            setRetryIdNumber(info.idNumberDestino || '');
            setRetryBeneficiaryName(info.beneficiaryName || '');
            setIsRetryMode(true);
            setIsQuoteConfirmed(true);
            setActiveStep(2);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (activeInput === 'USDT') {
                if (montoUSDT && parseFloat(montoUSDT) > 0) {
                    handlePreview('USDT', parseFloat(montoUSDT));
                } else if (!montoUSDT) {
                    resetCalculatedValues();
                }
            }
        }, 800);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [montoUSDT, activeInput]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (activeInput === 'BS') {
                if (montoBs && parseFloat(montoBs) > 0) {
                    handlePreview('BS', parseFloat(montoBs));
                } else if (!montoBs) {
                    resetCalculatedValues();
                }
            }
        }, 800);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [montoBs, activeInput]);

    const resetCalculatedValues = () => {
        setMontoPagar('0,000000');
        setMontoRecibir('0,00');
        setPreviewData(null);
        setQuoteData(null);
        setExternalId('');
        setInsufficientFunds(false);
    };

    const handlePreview = async (currency, amount) => {
        if (!amount || amount <= 0) return;

        const balance = parseFloat(availableBalance);
        if (balance <= 0) {
            setErrorMessage('Saldo insuficiente. No puede realizar operaciones con balance 0.');
            setInsufficientFunds(true);
            return;
        }

        setInsufficientFunds(false);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v2/preview`,
                {
                    amount: amount,
                    currency: currency
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.token}`
                    }
                }
            );

            setPreviewData(response.data);

            // La tasa mostrada se carga una sola vez desde el backend con amount: 1 USDT.
            // Los previews por monto no deben modificarla.
            setMontoPagar(formatAmountUSDT(response.data.total_cost_usdt));
            setMontoRecibir(formatAmountBs(response.data.ves_amount));

            if (currency === 'USDT') {
                const val = parseFloat(response.data.ves_amount);
                setMontoBs(val ? val.toFixed(2) : '0.00');
            } else {
                const val = parseFloat(response.data.total_cost_usdt);
                setMontoUSDT(val ? val.toFixed(6) : '0.000000');
            }

        } catch (error) {
            if (error.response) {
                if (error.response.status === 403) {
                    setErrorMessage(BANK_UNAVAILABLE_MSG);
                    setMontoPagar('0,000000');
                    setMontoRecibir('0,00');
                    setPreviewData(null);
                } else if (error.response.data) {
                    if (error.response.data.message === 'INSUFFICIENT_FUNDS') {
                        setInsufficientFunds(true);
                        setMontoPagar('0,000000');
                        setMontoRecibir('0,00');
                        setPreviewData(null);
                        setErrorMessage(`Saldo insuficiente. Saldo disponible: ${formatAmountUSDT(availableBalance)} USDT`);
                    } else if (error.response.data.errorMessage) {
                        setErrorMessage(error.response.data.errorMessage);
                    }
                }
            } else {
                console.error(error);
            }
        }
    };

    const checkDiscrepancy = (preview, quote) => {
        const previewCost = parseFloat(preview.total_cost_usdt);
        const quoteCost = parseFloat(quote.total_cost_usdt);
        const previewVes = parseFloat(preview.ves_amount);
        const quoteVes = parseFloat(quote.ves_amount);

        const costDiff = Math.abs(previewCost - quoteCost) > 0.0001;
        const vesDiff = Math.abs(previewVes - quoteVes) > 0.01;

        return costDiff || vesDiff;
    };

    const MIN_USDT_AMOUNT = 1.5;

    const handleConfirmQuote = async () => {
        if (loadingConfirm) return;

        if (!previewData) {
            setErrorMessage('No hay datos de preview para confirmar');
            return;
        }

        const enteredUsdt = parseFloat(montoUSDT || 0);
        if (enteredUsdt < MIN_USDT_AMOUNT) {
            setErrorMessage(`El monto mínimo es ${MIN_USDT_AMOUNT} USDT. Por favor ingrese un monto mayor o igual.`);
            return;
        }

        const previewCost = parseFloat(previewData?.total_cost_usdt || 0);
        const balance = parseFloat(availableBalance);

        if (previewCost > balance) {
            setErrorMessage(`Saldo insuficiente. Monto a pagar: ${formatAmountUSDT(previewCost)} USDT, Saldo disponible: ${formatAmountUSDT(balance)} USDT`);
            return;
        }

        setLoadingConfirm(true);
        try {
            const newDynamicId = Math.random().toString(36).substring(2, 10);
            setExternalId(newDynamicId);

            const currency = activeInput;
            const amount = activeInput === 'USDT' ? parseFloat(montoUSDT) : parseFloat(montoBs);

            const quotePreviewResponse = await axios.post(
                `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v3/quote/preview`,
                {
                    amount: amount,
                    currency: currency,
                    external_id: newDynamicId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.token}`
                    }
                }
            );

            const quotePreviewData = quotePreviewResponse.data;

            if (checkDiscrepancy(previewData, quotePreviewData)) {
                setDiscrepancyData({
                    preview: previewData,
                    quote: quotePreviewData
                });
                setPendingQuoteData(quotePreviewData);
                setDiscrepancyDialogOpen(true);
                setLoadingConfirm(false);
                return;
            }

            await proceedWithConfirmation(quotePreviewData);

        } catch (error) {
            if (error.response) {
                if (error.response.status === 403) {
                    setErrorMessage(BANK_UNAVAILABLE_MSG);
                } else if (error.response.data) {
                    if (error.response.data.message === 'INSUFFICIENT_FUNDS') {
                        setErrorMessage(`Saldo insuficiente. Saldo disponible: ${formatAmountUSDT(availableBalance)} USDT`);
                    } else if (error.response.data.errorMessage) {
                        setErrorMessage(error.response.data.errorMessage);
                    } else {
                        setErrorMessage('Error al obtener la cotización');
                    }
                } else {
                    setErrorMessage('Error al obtener la cotización');
                }
            } else {
                setErrorMessage('Error al obtener la cotización');
            }
            setLoadingConfirm(false);
        }
    };

    const proceedWithConfirmation = async (quotePreviewData) => {
        try {
            const confirmResponse = await axios.put(
                `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v3/quote/confirm/${quotePreviewData.quote_id}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${session.token}`
                    }
                }
            );

            setQuoteData(confirmResponse.data);
            setIsQuoteConfirmed(true);
            setLoadingConfirm(false);
            // Si venimos de "Reusar", el flujo ya está elegido -> saltar el dialog.
            if (prefillFlowRef.current) {
                handleFlowSelection(prefillFlowRef.current);
                prefillFlowRef.current = null;
            } else {
                // Avanzar al paso 2 (selección de método de pago)
                setActiveStep(1);
            }
        } catch (error) {
            if (error.response && error.response.status === 403) {
                setErrorMessage(BANK_UNAVAILABLE_MSG);
            } else {
                setErrorMessage('Error al confirmar la cotización');
            }
            setLoadingConfirm(false);
        }
    };

    const handleFlowSelection = (flow) => {
        setSelectedFlow(flow);
        setSuccessMessage('Complete los datos del destinatario');
        setActiveStep(2);
    };

    const hasRecipientData = () => !!(phoneDestino || idNumberDestino || selectedBank || accountNumber || idNumberDestinoTransfer || beneficiaryName);

    // Permite cambiar de método. Si ya hay datos del destinatario cargados y se cambia a
    // OTRO método, pide confirmación porque se borrarán esos datos.
    const requestFlowSelection = (flow) => {
        if (selectedFlow && flow !== selectedFlow && hasRecipientData()) {
            setPendingMethod(flow);
            setMethodChangeOpen(true);
            return;
        }
        handleFlowSelection(flow);
    };

    const confirmMethodChange = () => {
        // Borrar los datos del destinatario para volver a cargarlos en el paso 3.
        setPhoneDestino(''); setIdNumberDestino(''); setSelectedBank('');
        setAccountNumber(''); setIdNumberDestinoTransfer(''); setBeneficiaryName('');
        setMethodChangeOpen(false);
        if (pendingMethod) handleFlowSelection(pendingMethod);
        setPendingMethod(null);
    };

    const handleTransferInfo = async () => {
        if (loadingTransfer) return;

        if (!externalId) {
            setErrorMessage('No hay identificador de cotización para enviar los datos');
            return;
        }

        if (!accountNumber || !idNumberDestinoTransfer || !beneficiaryName) {
            setErrorMessage('Por favor complete todos los campos requeridos');
            return;
        }

        if (!/^[VJE][0-9]+$/.test(idNumberDestinoTransfer)) {
            setErrorMessage('El formato de identificación debe iniciar con V, J o E seguido de números');
            return;
        }

        // Limpiar el timer al completar exitosamente
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
        }

        setLoadingTransfer(true);
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v3/quote/info/${externalId}`,
                {
                    accountNumber: accountNumber,
                    idNumberDestino: idNumberDestinoTransfer,
                    beneficiaryName: beneficiaryName
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.token}`
                    }
                }
            );

            if (response.data.success) {
                setSuccessMessage('Transferencia completada exitosamente');
                await refreshBalance();
                resetForm();
            }
        } catch (error) {
            if (error.response && error.response.status === 403) {
                setErrorMessage(BANK_UNAVAILABLE_MSG);
            } else {
                setErrorMessage(translateCashoutError(error));
            }
        } finally {
            setLoadingTransfer(false);
        }
    };

    const handleDiscrepancyConfirm = async () => {
        setDiscrepancyDialogOpen(false);
        if (pendingQuoteData) {
            setPreviewData(pendingQuoteData);
            setMontoPagar(formatAmountUSDT(pendingQuoteData.total_cost_usdt));
            setMontoRecibir(formatAmountBs(pendingQuoteData.ves_amount));
            setLoadingConfirm(true);
            await proceedWithConfirmation(pendingQuoteData);
        }
        setPendingQuoteData(null);
        setDiscrepancyData(null);
    };

    const handleRetrySubmit = async () => {
        if (!retryAccountNumber || !retryIdNumber || !retryBeneficiaryName) {
            setErrorMessage('Por favor complete todos los campos requeridos');
            return;
        }

        if (!/^[VJE][0-9]+$/.test(retryIdNumber)) {
            setErrorMessage('El formato de identificación debe iniciar con V, J o E seguido de números');
            return;
        }

        setLoadingRetry(true);
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v3/quote/info/${externalId}`,
                {
                    accountNumber: retryAccountNumber,
                    idNumberDestino: retryIdNumber,
                    beneficiaryName: retryBeneficiaryName
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.token}`
                    }
                }
            );

            if (response.data.success) {
                setSuccessMessage('Información enviada correctamente');
                await refreshBalance();
                resetForm();
                setIsRetryMode(false);
            }
        } catch (error) {
            if (error.response && error.response.status === 403) {
                setErrorMessage(BANK_UNAVAILABLE_MSG);
            } else {
                setErrorMessage(translateCashoutError(error, 'Error al reenviar la información'));
            }
        } finally {
            setLoadingRetry(false);
        }
    };

    const handleDiscrepancyCancel = async () => {
        // Cancelar la cotización en el servidor si existe
        if (pendingQuoteData && pendingQuoteData.quote_id) {
            try {
                await axios.put(
                    `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v3/quote/decline/${pendingQuoteData.quote_id}`,
                    {},
                    {
                        headers: {
                            'Authorization': `Bearer ${session.token}`
                        }
                    }
                );
            } catch (error) {
                // Si hay error al cancelar, lo registramos pero no bloqueamos el flujo
                console.error('Error al cancelar la cotización:', error);
            }
        }

        setDiscrepancyDialogOpen(false);
        setPendingQuoteData(null);
        setDiscrepancyData(null);
        if (activeInput === 'USDT' && montoUSDT) {
            handlePreview('USDT', parseFloat(montoUSDT));
        } else if (activeInput === 'BS' && montoBs) {
            handlePreview('BS', parseFloat(montoBs));
        }
    };

    const handleAddMobileInfo = async () => {
        if (loadingTransfer) return;

        if (!externalId) {
            setErrorMessage('No hay identificador de cotización para enviar los datos');
            return;
        }

        if (!phoneDestino || !idNumberDestino || !selectedBank) {
            setErrorMessage('Por favor complete todos los campos requeridos');
            return;
        }

        if (!isValidPhonePrefix(phoneDestino)) {
            setErrorMessage(`El teléfono debe comenzar con uno de los siguientes prefijos: ${PHONE_PREFIXES.join(', ')}`);
            return;
        }

        if (!isValidPhoneLength(phoneDestino)) {
            setErrorMessage('El teléfono debe tener 10 u 11 dígitos (ej. 04241234567). Verifica que no tenga dígitos de más.');
            return;
        }

        if (!/^[VJE][0-9]+$/.test(idNumberDestino)) {
            setErrorMessage('El formato de identificación debe iniciar con V, J o E seguido de números');
            return;
        }

        // Limpiar el timer al completar exitosamente
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
        }

        setLoadingTransfer(true);
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v3/quote/mobile/info/${externalId}`,
                {
                    phoneDestino: phoneDestino,
                    idNumberDestino: idNumberDestino,
                    BankAccount: selectedBank
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.token}`
                    }
                }
            );

            if (response.data.success) {
                setSuccessMessage('Datos del receptor enviados correctamente');
                await refreshBalance();
                resetForm();
            }
        } catch (error) {
            if (error.response && error.response.status === 403) {
                setErrorMessage(BANK_UNAVAILABLE_MSG);
            } else {
                setErrorMessage(translateCashoutError(error));
            }
        } finally {
            setLoadingTransfer(false);
        }
    };

    const refreshBalance = useCallback(async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_URL_API_CRIXTO}orders/transactions/v2/balance`,
                {
                    headers: {
                        'Authorization': `Bearer ${session.token}`
                    }
                }
            );
            if (response.data && response.data.data) {
                setAvailableBalance(truncateToDecimals(parseFloat(response.data.data.available), 6));
            }
        } catch (error) {
            console.error('Error al actualizar el saldo');
        }
    }, [session.token]);

    // Actualizar balance cuando la página recupera foco o visibilidad
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && balanceLoaded) {
                refreshBalance();
            }
        };

        const handleFocus = () => {
            if (balanceLoaded) {
                refreshBalance();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [balanceLoaded, refreshBalance]);


    const resetForm = () => {
        // Limpiar el timer
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
        }
        setTimeRemaining(120);
        
        setMontoUSDT('');
        setMontoBs('');
        resetCalculatedValues();
        setIsQuoteConfirmed(false);
        setPhoneDestino('');
        setIdNumberDestino('');
        setSelectedBank('');
        setAccountNumber('');
        setIdNumberDestinoTransfer('');
        setBeneficiaryName('');
        setSelectedFlow(null);
        setActiveTab(0);
        setActiveStep(0);
        setActiveInput('USDT');
        setLoadingConfirm(false);
        setLoadingTransfer(false);
        prefillFlowRef.current = null;
    };

    const SUCCESS_STATUSES = ['success', 'completed']; // matched case-insensitively

    const fmtRecDate = (ms) => ms
        ? new Date(ms).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '—';

    // Build the recurring-clients table from the partner's successful quotes.
    // One row per recipient (idNumberDestino), keeping the latest Pago Móvil and
    // the latest wire-transfer info, ordered by last activity.
    const fetchRecurrentes = async () => {
        setLoadingRecurrentes(true);
        try {
            const auth = { headers: { Authorization: `Bearer ${session.token}` } };
            const PAGE_SIZE = 100; // report validator caps limit at 100; table still shows 20/page
            const byClient = {};
            const addRow = (r) => {
                if (!SUCCESS_STATUSES.includes((r.status || '').toLowerCase())) return;
                const info = r.receiver_info || {};
                const id = (info.idNumberDestino || '').trim();
                if (!id) return;
                const when = new Date(r.last_updated_at || 0).getTime();
                if (!byClient[id]) byClient[id] = { id, name: '', lastActivity: 0, count: 0, pm: null, wire: null };
                const c = byClient[id];
                c.count += 1;
                if (when > c.lastActivity) c.lastActivity = when;
                if (info.phoneDestino) {
                    if (!c.pm || when > c.pm.when) c.pm = { phone: info.phoneDestino, bankName: info.bank_name || info.BankAccount || '', when };
                }
                if (info.accountNumber || info.beneficiaryName) {
                    if (!c.wire || when > c.wire.when) c.wire = { account: info.accountNumber || '', name: info.beneficiaryName || '', when };
                    if (info.beneficiaryName && !c.name) c.name = info.beneficiaryName;
                }
            };
            // Scan the FULL history, fetching the report in pages of 20 and following
            // its pagination until there are no more pages (guard caps a runaway loop).
            let page = 1, hasNext = true, guard = 0;
            while (hasNext && guard < 5000) {
                guard += 1;
                // Mirror the proven-working /transactions call exactly: REACT_APP_URL_API
                // base, NO trailing slash before '?'. (The _CRIXTO base + 'report/?' form 400'd.)
                const res = await axios.get(
                    `${process.env.REACT_APP_URL_API}/orders/transactions/v2/report?page=${page}&limit=${PAGE_SIZE}`,
                    auth
                );
                if (!res.data?.success) break;
                const rows = res.data.data || [];
                rows.forEach(addRow);
                const pg = res.data.pagination || {};
                hasNext = pg.hasNext === true && rows.length > 0;
                page += 1;
            }
            setRecurrentes(Object.values(byClient).sort((a, b) => b.lastActivity - a.lastActivity));
            setRecPage(1);
        } catch (e) {
            setErrorMessage('Error al obtener los clientes recurrentes');
        } finally {
            setLoadingRecurrentes(false);
        }
    };

    // The report returns the bank NAME (formatCashoutQuote overwrites BankAccount);
    // map it back to the bank code the Pago Móvil selector expects.
    const bankCodeFromName = (name) => {
        if (!name) return '';
        const list = Array.isArray(banks) ? banks : [];
        const m = list.find((b) => b.nombre === name || b.name === name || (b.codigo && String(name).includes(b.codigo)));
        return m ? m.codigo : '';
    };

    // "Reusar": clean slate, prefill the recipient + flow, jump to Cotización.
    // The operator only enters the new amount; the flow dialog is skipped on confirm.
    const reusePagoMovil = (c) => {
        if (!c || !c.pm) return;
        resetForm();
        setSelectedFlow('mobile');
        prefillFlowRef.current = 'mobile';
        setPhoneDestino(c.pm.phone || '');
        setIdNumberDestino(c.id || '');
        setSelectedBank(bankCodeFromName(c.pm.bankName));
        setActiveTab(0);
        setSuccessMessage('Datos del destinatario precargados (Pago Móvil). Ingresa el monto y confirma.');
    };

    const reuseTransfer = (c) => {
        if (!c || !c.wire) return;
        resetForm();
        setSelectedFlow('transfer');
        prefillFlowRef.current = 'transfer';
        setAccountNumber(c.wire.account || '');
        setIdNumberDestinoTransfer(c.id || '');
        setBeneficiaryName(c.wire.name || '');
        setActiveTab(0);
        setSuccessMessage('Datos del destinatario precargados (Transferencia). Ingresa el monto y confirma.');
    };


    const truncateToDecimals = (num, decimals) => {
        const factor = Math.pow(10, decimals);
        return Math.trunc(num * factor) / factor;
    };

    const formatAmountUSDT = (value) => {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return new Intl.NumberFormat('es-ES', {
            minimumFractionDigits: 6,
            maximumFractionDigits: 6
        }).format(numValue || 0);
    };

    const formatAmountBs = (value) => {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return new Intl.NumberFormat('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numValue || 0);
    };

    const handleUSDTChange = (value) => {
        const regex = /^\d*\.?\d{0,6}$/;
        if (regex.test(value) || value === '') {
            setMontoUSDT(value);
            setActiveInput('USDT');
            if (value && parseFloat(value) > 0) {
                setMontoBs('0');
            } else {
                setMontoBs('');
            }
        }
    };

    const handleBsChange = (value) => {
        const regex = /^\d*\.?\d{0,2}$/;
        if (regex.test(value) || value === '') {
            setMontoBs(value);
            setActiveInput('BS');
            if (value && parseFloat(value) > 0) {
                setMontoUSDT('0');
            } else {
                setMontoUSDT('');
            }
        }
    };

    const getDisplayMontoEnviar = () => {
        if (activeInput === 'BS') {
            const val = montoBs && parseFloat(montoBs) > 0 ? parseFloat(montoBs) : 0;
            return `${formatAmountBs(val)} VES`;
        } else {
            const val = montoUSDT && parseFloat(montoUSDT) > 0 ? parseFloat(montoUSDT) : 0;
            return `${formatAmountUSDT(val)} USDT`;
        }
    };


    const isBelowMinAmount = () => {
        const val = parseFloat(montoUSDT || 0);
        return val > 0 && val < MIN_USDT_AMOUNT;
    };

    const isConfirmDisabled = () => {
        if (loadingConfirm || !previewData || isQuoteConfirmed || insufficientFunds) return true;
        if (isBelowMinAmount()) return true;
        const totalCost = parseFloat(previewData?.total_cost_usdt || 0);
        const balance = parseFloat(availableBalance);
        return totalCost > balance || totalCost <= 0;
    };

    const PHONE_PREFIXES = ['0414', '0416', '0412', '0422', '0424', '0426'];

    const isValidPhonePrefix = (phone) => {
        if (!phone || phone.length < 4) return false;
        return PHONE_PREFIXES.some(prefix => phone.startsWith(prefix));
    };

    // PagoMóvil (VE): el backend acepta 10 u 11 dígitos. Validar en cliente para evitar
    // rechazos genéricos al completar (ej. un dígito de más => "Error al completar la transferencia").
    const VALID_PHONE_LENGTHS = [10, 11];
    const isValidPhoneLength = (phone) => {
        const digits = (phone || '').replace(/[^0-9]/g, '');
        return VALID_PHONE_LENGTHS.includes(digits.length);
    };

    const isTransferDisabled = () => {
        if (selectedFlow === 'mobile') {
            if (loadingTransfer || !phoneDestino || !idNumberDestino || !selectedBank) return true;
            if (!isValidPhonePrefix(phoneDestino)) return true;
            if (!isValidPhoneLength(phoneDestino)) return true;
            return false;
        } else if (selectedFlow === 'transfer') {
            return loadingTransfer || !accountNumber || !idNumberDestinoTransfer || !beneficiaryName;
        }
        return true;
    };

    const getTimerColor = () => {
        return theme.vars.palette.primary[500];
    };

    // Vertical stepper: a step is locked until the previous one is validated.
    const stepLocked = (idx) => {
        if (idx === 0) return false;                            // Cotización: siempre clickeable (reinicia la operación)
        if (idx === 1) return !isQuoteConfirmed;                 // Método: requiere cotización confirmada
        if (idx === 2) return !(selectedFlow || isRetryMode);    // Destinatario: requiere método elegido
        return true;
    };
    const goStep = (idx) => { if (!stepLocked(idx)) setActiveStep(idx); };
    // Click en el paso 1 desde otro paso = reiniciar la operación (cotización nueva desde cero).
    const handleStepClick = (idx) => {
        if (idx === 0) {
            if (activeStep !== 0 && (isQuoteConfirmed || montoUSDT || montoBs)) setRestartConfirmOpen(true);
            else setActiveStep(0);
            return;
        }
        goStep(idx);
    };
    const STEP_TITLES = ['Cotización', 'Método de pago', 'Información del Destinatario'];
    // Inactive steps collapse to a compact one-line summary (keeps the selection visible
    // without the full form, so the page stays short). Click the header to reopen/edit.
    const renderStepSummary = (idx) => {
        let text = '—';
        if (idx === 0) text = (montoUSDT || montoBs) ? `${montoUSDT || '—'} USDT  ·  ${montoBs || '—'} VES` : 'Sin cotización';
        else if (idx === 1) text = selectedFlow === 'mobile' ? 'Pago Móvil' : selectedFlow === 'transfer' ? 'Transferencia Bancaria' : 'Sin seleccionar';
        else if (idx === 2) {
            if (selectedFlow === 'mobile' && phoneDestino) text = phoneDestino;
            else if (selectedFlow === 'transfer' && (accountNumber || beneficiaryName)) text = `${accountNumber || ''} ${beneficiaryName || ''}`.trim();
            else text = 'Sin completar';
        }
        return (
            <Box sx={{ px: 5, pb: 2, pt: 0.5 }}>
                <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', color: theme.vars.palette.text.primary }}>{text}</Typography>
            </Box>
        );
    };
    const renderStepHeader = (idx) => {
        const active = activeStep === idx;
        const done = activeStep > idx;
        const locked = stepLocked(idx);
        return (
            <Box
                ref={stepRefs[idx]}
                onClick={() => handleStepClick(idx)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 1,
                    cursor: locked ? 'default' : 'pointer',
                    opacity: locked ? 0.4 : 1,
                    borderLeft: `3px solid ${active ? theme.vars.palette.primary[500] : 'transparent'}`,
                    scrollMarginTop: '16px',
                }}
            >
                <Box sx={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 700,
                    color: (active || done) ? theme.vars.palette.background.body : theme.vars.palette.primary[500],
                    backgroundColor: (active || done) ? theme.vars.palette.primary[500] : 'transparent',
                    border: `2px solid ${theme.vars.palette.primary[500]}`,
                }}>
                    {done ? '✓' : idx + 1}
                </Box>
                <Typography sx={{
                    fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 700,
                    color: active ? theme.vars.palette.primary[500] : theme.vars.palette.text.main,
                }}>
                    {STEP_TITLES[idx]}
                </Typography>
            </Box>
        );
    };

    return (
        <Box mx={5} my={2} sx={{ width: CONTENT_WIDTH }}>
            <Snackbar
                open={!!errorMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                variant="solid"
                color="danger"
                autoHideDuration={4000}
                onClose={() => setErrorMessage('')}
            >
                {errorMessage}
            </Snackbar>

            <Snackbar
                open={!!successMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                variant="solid"
                color="success"
                autoHideDuration={4000}
                onClose={() => setSuccessMessage('')}
            >
                {successMessage}
            </Snackbar>

            <Modal open={discrepancyDialogOpen} onClose={handleDiscrepancyCancel}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 500 }}>
                    <ModalClose />
                    <DialogTitle sx={{ fontFamily: "Montserrat", fontWeight: 700, color: theme.vars.palette.warning[500] }}>
                        ⚠️ Discrepancia en la Cotización
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        <Typography sx={{ fontFamily: "Montserrat", mb: 2, color: theme.vars.palette.text.main }}>
                            Los valores de la cotización han cambiado desde el preview inicial. Por favor revise las diferencias:
                        </Typography>

                        {discrepancyData && (
                            <Box sx={{
                                backgroundColor: theme.vars.palette.background.level1,
                                borderRadius: '8px',
                                p: 2,
                                mb: 2
                            }}>
                                <Grid container spacing={2}>
                                    <Grid xs={6}>
                                        <Typography sx={{ fontFamily: "Montserrat", fontWeight: 600, mb: 1, color: theme.vars.palette.text.main }}>
                                            Preview Inicial
                                        </Typography>
                                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                            Costo USDT: <strong>{formatAmountUSDT(discrepancyData.preview.total_cost_usdt)}</strong>
                                        </Typography>
                                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                            Monto VES: <strong>{formatAmountBs(discrepancyData.preview.ves_amount)}</strong>
                                        </Typography>
                                    </Grid>
                                    <Grid xs={6}>
                                        <Typography sx={{ fontFamily: "Montserrat", fontWeight: 600, mb: 1, color: theme.vars.palette.primary[500] }}>
                                            Cotización Actual
                                        </Typography>
                                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                            Costo USDT: <strong style={{
                                                color:
                                                    parseFloat(discrepancyData.quote.total_cost_usdt) !== parseFloat(discrepancyData.preview.total_cost_usdt)
                                                        ? theme.vars.palette.warning[500]
                                                        : 'inherit'
                                            }}>
                                                {formatAmountUSDT(discrepancyData.quote.total_cost_usdt)}
                                            </strong>
                                        </Typography>
                                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                            Monto VES: <strong style={{
                                                color:
                                                    parseFloat(discrepancyData.quote.ves_amount) !== parseFloat(discrepancyData.preview.ves_amount)
                                                        ? theme.vars.palette.warning[500]
                                                        : 'inherit'
                                            }}>
                                                {formatAmountBs(discrepancyData.quote.ves_amount)}
                                            </strong>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                            ¿Desea continuar con los nuevos valores?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <SecondaryButton
                            click={handleDiscrepancyCancel}
                            text="Cancelar"
                        />
                        <PrimaryButton
                            click={handleDiscrepancyConfirm}
                            text="Confirmar"
                        />
                    </DialogActions>
                </ModalDialog>
            </Modal>

            {/* Dialog de selección de flujo de pago */}
            {/* Confirmación al cambiar de método (se borran los datos del destinatario) */}
            {/* Confirmación al reiniciar desde el paso 1 (Cotización) */}
            <Modal open={restartConfirmOpen} onClose={() => setRestartConfirmOpen(false)}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 460 }}>
                    <DialogTitle sx={{ fontFamily: "Montserrat", fontWeight: 700, color: theme.vars.palette.primary[500] }}>
                        ¿Reiniciar la operación?
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        <Typography sx={{ fontFamily: "Montserrat", color: theme.vars.palette.text.main }}>
                            Volver a la Cotización inicia una operación nueva desde cero: se perderá la cotización actual y los datos del destinatario que hayas cargado.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <SecondaryButton click={() => setRestartConfirmOpen(false)} text="Cancelar" />
                        <PrimaryButton click={() => { setRestartConfirmOpen(false); resetForm(); }} text="Sí, reiniciar" />
                    </DialogActions>
                </ModalDialog>
            </Modal>

            <Modal open={methodChangeOpen} onClose={() => { setMethodChangeOpen(false); setPendingMethod(null); }}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 460 }}>
                    <DialogTitle sx={{ fontFamily: "Montserrat", fontWeight: 700, color: theme.vars.palette.primary[500] }}>
                        ¿Estás seguro?
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        <Typography sx={{ fontFamily: "Montserrat", color: theme.vars.palette.text.main }}>
                            Cambiar a {pendingMethod === 'mobile' ? 'Pago Móvil' : 'Transferencia Bancaria'} borrará los datos del destinatario que ya cargaste. Deberás completarlos de nuevo en el paso 3.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <SecondaryButton click={() => { setMethodChangeOpen(false); setPendingMethod(null); }} text="Cancelar" />
                        <PrimaryButton click={confirmMethodChange} text="Sí, cambiar" />
                    </DialogActions>
                </ModalDialog>
            </Modal>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Typography sx={{
                        fontFamily: "Montserrat",
                        fontSize: "20px",
                        fontStyle: "normal",
                        fontWeight: 700,
                        lineHeight: "120%",
                        color: theme.vars.palette.primary["500"],
                    }}>
                        Operaciones manuales - {formatAmountUSDT(availableBalance)} USDT
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.5,
                        borderRadius: '8px',
                        backgroundColor: theme.vars.palette.background.level1,
                        border: `1px solid ${theme.vars.palette.neutral[300]}`
                    }}>
                        <Typography sx={{
                            fontFamily: "Montserrat",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: theme.vars.palette.text.primary,
                        }}>
                            Tasa:
                        </Typography>
                        <Typography sx={{
                            fontFamily: "Montserrat",
                            fontSize: "14px",
                            fontWeight: 700,
                            color: theme.vars.palette.primary["500"],
                        }}>
                            {loadingFee ? '...' : platformFee !== null ? `${formatAmountBs(platformFee)} VES` : '-'}
                        </Typography>
                    </Box>
                </Box>

                {balanceLoaded && parseFloat(availableBalance) <= 0 && (
                    <Box sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: "8px",
                        backgroundColor: theme.vars.palette.danger[100],
                        border: `1px solid ${theme.vars.palette.danger[500]}`
                    }}>
                        <Typography sx={{
                            fontFamily: "Montserrat",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: theme.vars.palette.danger[500]
                        }}>
                            No tiene saldo disponible para realizar operaciones.
                        </Typography>
                    </Box>
                )}

            </Box>

                <Box mt={2}>
                    <Tabs
                        value={activeTab}
                        onChange={(event, newValue) => {
                            if (newValue === 1 && !recurrentesFetchedRef.current) {
                                recurrentesFetchedRef.current = true;
                                fetchRecurrentes();
                            }
                            setActiveTab(newValue);
                        }}
                        sx={{ width: '100%' }}
                    >
                        <TabList sx={{ mb: 3 }}>
                            <Tab
                                sx={{ fontFamily: "Montserrat", fontWeight: 600, color: theme.vars.palette.primary["500"] }}
                            >
                                Operación
                            </Tab>
                            <Tab
                                sx={{ fontFamily: "Montserrat", fontWeight: 600, color: theme.vars.palette.primary["500"] }}
                            >
                                Recurrentes
                            </Tab>
                        </TabList>

                        <TabPanel value={0} sx={{ p: 0 }}>
                            {/* ===== STEP 1: Cotización ===== */}
                            {renderStepHeader(0)}
                            {activeStep === 0 ? (
                            <Grid container spacing={3} p={4}>
                                <Grid xs={12} md={6}>
                                    <Typography mb={1} sx={{
                                        fontFamily: "Montserrat",
                                        fontSize: "16px",
                                        fontWeight: 500,
                                        color: theme.vars.palette.text.main,
                                    }}>
                                        Monto USDT
                                    </Typography>
                                    <CustomInput
                                        placeholder="0.000000 USDT"
                                        width="100%"
                                        value={montoUSDT}
                                        setValue={handleUSDTChange}
                                        disabled={activeInput === 'BS' && montoBs && parseFloat(montoBs) > 0}
                                    />
                                    <Typography sx={{
                                        mt: 0.5,
                                        fontSize: "12px",
                                        fontFamily: "Montserrat",
                                        color: theme.vars.palette.text.main
                                    }}>
                                        {activeInput === 'VES' ? 'Calculando desde VES...' : 'Ingrese el monto en USDT'}
                                    </Typography>
                                    {isBelowMinAmount() && (
                                        <Typography sx={{
                                            mt: 0.5,
                                            fontSize: "12px",
                                            fontFamily: "Montserrat",
                                            fontWeight: 600,
                                            color: theme.vars.palette.warning[600] || '#E65100'
                                        }}>
                                            ⚠️ El monto mínimo es {MIN_USDT_AMOUNT} USDT
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid xs={12} md={6}>
                                    <Typography mb={1} sx={{
                                        fontFamily: "Montserrat",
                                        fontSize: "16px",
                                        fontWeight: 500,
                                        color: theme.vars.palette.text.main,
                                    }}>
                                        Monto VES
                                    </Typography>
                                    <CustomInput
                                        placeholder="0.00"
                                        width="100%"
                                        value={montoBs}
                                        setValue={handleBsChange}
                                        disabled={activeInput === 'USDT' && montoUSDT && parseFloat(montoUSDT) > 0}
                                    />
                                    <Typography sx={{
                                        mt: 0.5,
                                        fontSize: "12px",
                                        fontFamily: "Montserrat",
                                        color: theme.vars.palette.text.main
                                    }}>
                                        {activeInput === 'USDT' ? 'Calculando desde USDT...' : 'Ingrese el monto en Bolívares'}
                                    </Typography>
                                </Grid>

                                <Grid xs={12}>
                                    <Box sx={{
                                        mt: 4,
                                        p: 3,
                                        borderRadius: "12px",
                                        backgroundColor: theme.vars.palette.background.level1,
                                        border: `1px solid ${insufficientFunds ? theme.vars.palette.danger[500] : theme.vars.palette.neutral[200]}`
                                    }}>
                                        {insufficientFunds && (
                                            <Typography sx={{
                                                fontFamily: "Montserrat",
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                color: theme.vars.palette.danger[500],
                                                mb: 2
                                            }}>
                                                Saldo insuficiente para realizar esta operación
                                            </Typography>
                                        )}
                                        <Grid container spacing={3}>
                                            <Grid xs={12} md={4}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography sx={{
                                                        fontFamily: "Montserrat",
                                                        fontSize: "16px",
                                                        fontWeight: 500,
                                                        color: theme.vars.palette.text.main
                                                    }}>
                                                        Monto a enviar
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontFamily: "Montserrat",
                                                        fontSize: "18px",
                                                        fontWeight: 700,
                                                        color: theme.vars.palette.primary["500"]
                                                    }}>
                                                        {getDisplayMontoEnviar()}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid xs={12} md={4}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography sx={{
                                                        fontFamily: "Montserrat",
                                                        fontSize: "16px",
                                                        fontWeight: 500,
                                                        color: theme.vars.palette.text.main
                                                    }}>
                                                        Monto a pagar
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontFamily: "Montserrat",
                                                        fontSize: "18px",
                                                        fontWeight: 700,
                                                        color: insufficientFunds ? theme.vars.palette.danger[500] : theme.vars.palette.primary["500"]
                                                    }}>
                                                        {montoPagar} USDT
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid xs={12} md={4}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography sx={{
                                                        fontFamily: "Montserrat",
                                                        fontSize: "16px",
                                                        fontWeight: 500,
                                                        color: theme.vars.palette.text.main
                                                    }}>
                                                        Monto a recibir
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontFamily: "Montserrat",
                                                        fontSize: "18px",
                                                        fontWeight: 700,
                                                        color: insufficientFunds ? theme.vars.palette.danger[500] : theme.vars.palette.primary["500"]
                                                    }}>
                                                        {montoRecibir} VES
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>

                                <Grid xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                        <SecondaryButton
                                            click={resetForm}
                                            text="Limpiar"
                                            desactived={!montoUSDT && !montoBs}
                                        />
                                        <PrimaryButton
                                            click={handleConfirmQuote}
                                            text="Confirmar y Continuar"
                                            loading={loadingConfirm}
                                            desactived={isConfirmDisabled()}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            ) : renderStepSummary(0)}
                            {/* ===== STEP 2: Método de pago ===== */}
                            {renderStepHeader(1)}
                            {activeStep === 1 ? (
                                <Box p={4} sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 520 }}>
                                    <Typography sx={{ fontFamily: "Montserrat", fontSize: "15px", color: theme.vars.palette.text.main, mb: 1 }}>
                                        ¿Cómo desea realizar el pago?
                                    </Typography>
                                    <Button variant={selectedFlow === 'mobile' ? 'solid' : 'outlined'} color="primary" onClick={() => requestFlowSelection('mobile')} sx={{ p: 2, fontFamily: "Montserrat", fontSize: "16px", fontWeight: 600, justifyContent: 'flex-start', borderRadius: '12px' }}>
                                        Pago Móvil {selectedFlow === 'mobile' ? '✓' : ''}
                                    </Button>
                                    <Button variant={selectedFlow === 'transfer' ? 'solid' : 'outlined'} color="primary" onClick={() => requestFlowSelection('transfer')} sx={{ p: 2, fontFamily: "Montserrat", fontSize: "16px", fontWeight: 600, justifyContent: 'flex-start', borderRadius: '12px' }}>
                                        Transferencia Bancaria {selectedFlow === 'transfer' ? '✓' : ''}
                                    </Button>
                                </Box>
                            ) : renderStepSummary(1)}
                            {/* ===== STEP 3: Información del Destinatario ===== */}
                            {renderStepHeader(2)}
                            {activeStep === 2 ? (
                            isRetryMode ? (
                                /* Formulario de Reintentar */
                                <Grid container spacing={3} p={4}>
                                    <Grid xs={12}>
                                        <Box sx={{
                                            p: 2,
                                            borderRadius: '12px',
                                            backgroundColor: theme.vars.palette.warning[50],
                                            border: `1px solid ${theme.vars.palette.warning[400]}`,
                                            mb: 1
                                        }}>
                                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", fontWeight: 600, color: theme.vars.palette.warning[700] }}>
                                                Reintentando transacción: {externalId}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid xs={12}>
                                        <Typography mb={1} sx={{ fontFamily: "Montserrat", fontSize: "16px", fontWeight: 500, color: theme.vars.palette.text.main }}>
                                            Número de Cuenta
                                        </Typography>
                                        <CustomInput
                                            placeholder="01050027381027441777"
                                            width="100%"
                                            value={retryAccountNumber}
                                            setValue={setRetryAccountNumber}
                                        />
                                    </Grid>

                                    <Grid xs={12} md={6}>
                                        <Typography mb={1} sx={{ fontFamily: "Montserrat", fontSize: "16px", fontWeight: 500, color: theme.vars.palette.text.main }}>
                                            Identificación del Destinatario
                                        </Typography>
                                        <CustomInput
                                            placeholder="V18184460"
                                            width="100%"
                                            value={retryIdNumber}
                                            setValue={setRetryIdNumber}
                                        />
                                        <Typography sx={{ mt: 0.5, fontSize: "12px", fontFamily: "Montserrat", color: theme.vars.palette.text.main }}>
                                            Debe iniciar con V, J o E seguido de números
                                        </Typography>
                                    </Grid>

                                    <Grid xs={12} md={6}>
                                        <Typography mb={1} sx={{ fontFamily: "Montserrat", fontSize: "16px", fontWeight: 500, color: theme.vars.palette.text.main }}>
                                            Nombre del Beneficiario
                                        </Typography>
                                        <CustomInput
                                            placeholder="Usuario de Prueba"
                                            width="100%"
                                            value={retryBeneficiaryName}
                                            setValue={setRetryBeneficiaryName}
                                        />
                                    </Grid>

                                    <Grid xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                            <PrimaryButton
                                                click={handleRetrySubmit}
                                                text="Reenviar Información"
                                                loading={loadingRetry}
                                                desactived={!retryAccountNumber || !retryIdNumber || !retryBeneficiaryName || loadingRetry}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            ) : (
                            <Grid container spacing={3} p={4}>
                                {/* Timer Display */}
                                <Grid xs={12}>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        mb: 3,
                                        p: 2,
                                        borderRadius: '12px',
                                        backgroundColor: timeRemaining <= 30 
                                            ? 'red'
                                            : timeRemaining <= 60 
                                                ? 'orange' 
                                                : theme.vars.palette.background.level1,
                                        border: `2px solid ${getTimerColor()}`,
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Typography sx={{
                                                fontFamily: "Montserrat",
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                color: theme.vars.palette.primary[500],
                                            }}>
                                                Tiempo restante para completar:
                                            </Typography>
                                            <Typography sx={{
                                                fontFamily: "Montserrat",
                                                fontSize: "24px",
                                                fontWeight: 700,
                                                color: getTimerColor(),
                                                minWidth: '70px',
                                                textAlign: 'center',
                                            }}>
                                                {formatTime(timeRemaining)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {timeRemaining <= 60 && (
                                        <Typography sx={{
                                            fontFamily: "Montserrat",
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            color: getTimerColor(),
                                            textAlign: 'center',
                                            mb: 2,
                                        }}>
                                            Complete los datos antes de que expire el tiempo.
                                        </Typography>
                                    )}
                                </Grid>

                                {/* Formulario según el flujo seleccionado */}
                                {selectedFlow === 'mobile' ? (
                                    <>
                                        <Grid xs={12} md={6}>
                                            <Typography mb={1} sx={{
                                                fontFamily: "Montserrat",
                                                fontSize: "16px",
                                                fontWeight: 500,
                                                color: theme.vars.palette.text.main,
                                            }}>
                                                Teléfono del Destinatario
                                            </Typography>
                                            <CustomInput
                                                placeholder="04244445566"
                                                width="100%"
                                                value={phoneDestino}
                                                setValue={setPhoneDestino}
                                            />
                                            <Typography sx={{
                                                mt: 0.5,
                                                fontSize: "12px",
                                                fontFamily: "Montserrat",
                                                color: theme.vars.palette.text.main
                                            }}>
                                                Prefijos válidos: {PHONE_PREFIXES.join(', ')} · 10 u 11 dígitos
                                            </Typography>
                                            {phoneDestino && phoneDestino.length >= 4 && !isValidPhonePrefix(phoneDestino) && (
                                                <Typography sx={{
                                                    mt: 0.5,
                                                    fontSize: "12px",
                                                    fontFamily: "Montserrat",
                                                    fontWeight: 600,
                                                    color: theme.vars.palette.warning[600] || '#E65100'
                                                }}>
                                                    ⚠️ El teléfono debe comenzar con: {PHONE_PREFIXES.join(', ')}
                                                </Typography>
                                            )}
                                            {phoneDestino && isValidPhonePrefix(phoneDestino) && !isValidPhoneLength(phoneDestino) && (
                                                <Typography sx={{
                                                    mt: 0.5,
                                                    fontSize: "12px",
                                                    fontFamily: "Montserrat",
                                                    fontWeight: 600,
                                                    color: theme.vars.palette.warning[600] || '#E65100'
                                                }}>
                                                    ⚠️ El teléfono debe tener 10 u 11 dígitos ({(phoneDestino || '').replace(/[^0-9]/g, '').length} ingresados).
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid xs={12} md={6}>
                                            <Typography mb={1} sx={{
                                                fontFamily: "Montserrat",
                                                fontSize: "16px",
                                                fontWeight: 500,
                                                color: theme.vars.palette.text.main,
                                            }}>
                                                Identificación del Destinatario
                                            </Typography>
                                            <CustomInput
                                                placeholder="V18184460"
                                                width="100%"
                                                value={idNumberDestino}
                                                setValue={setIdNumberDestino}
                                            />
                                            <Typography sx={{
                                                mt: 0.5,
                                                fontSize: "12px",
                                                fontFamily: "Montserrat",
                                                color: theme.vars.palette.text.main
                                            }}>
                                                Debe iniciar con V, J o E seguido de números
                                            </Typography>
                                        </Grid>

                                        <Grid xs={12}>
                                            <Typography mb={1} sx={{
                                                fontFamily: "Montserrat",
                                                fontSize: "16px",
                                                fontWeight: 500,
                                                color: theme.vars.palette.text.main,
                                            }}>
                                                Banco del Destinatario
                                            </Typography>
                                            <Autocomplete
                                                options={Array.isArray(banks) ? banks : []}
                                                getOptionLabel={(bank) => (bank && bank.codigo ? `${bank.codigo} - ${bank.nombre || ''}` : (bank?.nombre || ''))}
                                                isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                                                value={banks.find((b) => b.codigo === selectedBank) || null}
                                                onChange={(_, newValue) => setSelectedBank(newValue ? newValue.codigo : '')}
                                                placeholder="Buscar por código o nombre..."
                                                sx={{
                                                    width: '100%',
                                                    '--Input-radius': '12px',
                                                    borderRadius: "12px",
                                                    border: "2px solid #18E9E9",
                                                    backgroundColor: theme.vars.palette.background.body,
                                                    fontFamily: "Montserrat",
                                                    fontSize: "16px",
                                                    fontWeight: 600,
                                                }}
                                            />
                                        </Grid>
                                    </>
                                ) : selectedFlow === 'transfer' ? (
                                    <>
                                        <Grid xs={12}>
                                            <Typography mb={1} sx={{
                                                fontFamily: "Montserrat",
                                                fontSize: "16px",
                                                fontWeight: 500,
                                                color: theme.vars.palette.text.main,
                                            }}>
                                                Número de Cuenta
                                            </Typography>
                                            <CustomInput
                                                placeholder="01380040500400254789"
                                                width="100%"
                                                value={accountNumber}
                                                setValue={setAccountNumber}
                                            />
                                        </Grid>

                                        <Grid xs={12} md={6}>
                                            <Typography mb={1} sx={{
                                                fontFamily: "Montserrat",
                                                fontSize: "16px",
                                                fontWeight: 500,
                                                color: theme.vars.palette.text.main,
                                            }}>
                                                Identificación del Destinatario
                                            </Typography>
                                            <CustomInput
                                                placeholder="V18184460"
                                                width="100%"
                                                value={idNumberDestinoTransfer}
                                                setValue={setIdNumberDestinoTransfer}
                                            />
                                            <Typography sx={{
                                                mt: 0.5,
                                                fontSize: "12px",
                                                fontFamily: "Montserrat",
                                                color: theme.vars.palette.text.main
                                            }}>
                                                Debe iniciar con V, J o E seguido de números
                                            </Typography>
                                        </Grid>

                                        <Grid xs={12} md={6}>
                                            <Typography mb={1} sx={{
                                                fontFamily: "Montserrat",
                                                fontSize: "16px",
                                                fontWeight: 500,
                                                color: theme.vars.palette.text.main,
                                            }}>
                                                Nombre del Beneficiario
                                            </Typography>
                                            <CustomInput
                                                placeholder="Usuario de Prueba"
                                                width="100%"
                                                value={beneficiaryName}
                                                setValue={setBeneficiaryName}
                                            />
                                        </Grid>
                                    </>
                                ) : null}

                                {quoteData && (
                                    <Grid xs={12}>
                                        <Box sx={{
                                            mt: 3,
                                            p: 3,
                                            borderRadius: "12px",
                                            backgroundColor: theme.vars.palette.background.level1,
                                            border: `1px solid ${theme.vars.palette.neutral[200]}`
                                        }}>
                                            <Typography sx={{
                                                fontFamily: "Montserrat",
                                                fontSize: "16px",
                                                fontWeight: 600,
                                                mb: 2,
                                                color: theme.vars.palette.text.primary
                                            }}>
                                                Resumen de la transferencia
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid xs={6}>
                                                    <Typography sx={{
                                                        fontFamily: "Montserrat",
                                                        fontSize: "14px",
                                                        color: theme.vars.palette.text.primary
                                                    }}>
                                                        Monto USDT:
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontFamily: "Montserrat",
                                                        fontSize: "16px",
                                                        fontWeight: 600,
                                                        color: theme.vars.palette.primary["500"]
                                                    }}>
                                                        {formatAmountUSDT(quoteData.total_cost_usdt || quoteData.amount_usdt_to_send)} USDT
                                                    </Typography>
                                                </Grid>
                                                <Grid xs={6}>
                                                    <Typography sx={{
                                                        fontFamily: "Montserrat",
                                                        fontSize: "14px",
                                                        color: theme.vars.palette.text.primary
                                                    }}>
                                                        Bolívares a recibir:
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontFamily: "Montserrat",
                                                        fontSize: "16px",
                                                        fontWeight: 600,
                                                        color: theme.vars.palette.primary["500"]
                                                    }}>
                                                        {formatAmountBs(quoteData.ves_amount)} VES
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Grid>
                                )}

                                <Grid xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                        <PrimaryButton
                                            click={selectedFlow === 'mobile' ? handleAddMobileInfo : handleTransferInfo}
                                            text="Completar Transferencia"
                                            loading={loadingTransfer}
                                            desactived={isTransferDisabled()}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            )
                            ) : renderStepSummary(2)} {/* fin step 3 (Destinatario) */}
                        </TabPanel>

                        <TabPanel value={1} sx={{ p: 0 }}>
                            <Box p={4}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography sx={{ fontFamily: "Montserrat", fontSize: "16px", fontWeight: 700, color: theme.vars.palette.primary["500"] }}>
                                        Clientes recurrentes
                                    </Typography>
                                    <SecondaryButton click={fetchRecurrentes} text="Actualizar" />
                                </Box>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "12px", color: theme.vars.palette.text.main, mb: 3 }}>
                                    Clientes con transacciones exitosas, ordenados por última actividad. Usa "Reusar" para precargar los datos del destinatario y solo actualizar el monto.
                                </Typography>
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Box sx={{ minWidth: 1000 }}>
                                        <Grid container spacing={1} columns={12} sx={{ px: 2, py: 1.5, borderBottom: `2px solid ${theme.vars.palette.primary["500"]}` }}>
                                            {['Cliente (ID)', 'Nombre', 'Pago Móvil', 'Transferencia', 'Reusar'].map((h, i) => (
                                                <Grid key={h} xs={i === 2 || i === 3 ? 3 : (i === 4 ? 2 : 2)}>
                                                    <Typography sx={{ fontFamily: "Montserrat", fontSize: "12px", fontWeight: 700, color: theme.vars.palette.primary["500"] }}>{h}</Typography>
                                                </Grid>
                                            ))}
                                        </Grid>
                                        {loadingRecurrentes ? (
                                            <Box display="flex" justifyContent="center" py={4}>
                                                <Typography sx={{ fontFamily: "Montserrat", color: theme.vars.palette.text.main }}>Cargando clientes...</Typography>
                                            </Box>
                                        ) : recurrentes.length > 0 ? (
                                            <>
                                            {recurrentes.slice((recPage - 1) * 20, recPage * 20).map((c) => (
                                                <Grid key={c.id} container spacing={1} columns={12} sx={{ px: 2, py: 1.5, borderBottom: `1px solid rgba(255,255,255,0.08)`, alignItems: 'center' }}>
                                                    <Grid xs={2}>
                                                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", fontWeight: 600, color: theme.vars.palette.text.main }}>{c.id}</Typography>
                                                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "11px", color: theme.vars.palette.text.main, opacity: 0.7 }}>Última: {fmtRecDate(c.lastActivity)}</Typography>
                                                    </Grid>
                                                    <Grid xs={2}>
                                                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", color: theme.vars.palette.text.main }}>{c.name || '—'}</Typography>
                                                    </Grid>
                                                    <Grid xs={3}>
                                                        {c.pm ? (
                                                            <>
                                                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", color: theme.vars.palette.text.main }}>{c.pm.phone}</Typography>
                                                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "11px", color: theme.vars.palette.text.main, opacity: 0.7 }}>{c.pm.bankName || '—'}</Typography>
                                                            </>
                                                        ) : (<Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", color: theme.vars.palette.text.main, opacity: 0.5 }}>—</Typography>)}
                                                    </Grid>
                                                    <Grid xs={3}>
                                                        {c.wire ? (
                                                            <>
                                                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", color: theme.vars.palette.text.main }}>{c.wire.account || '—'}</Typography>
                                                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "11px", color: theme.vars.palette.text.main, opacity: 0.7 }}>{c.wire.name || '—'}</Typography>
                                                            </>
                                                        ) : (<Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", color: theme.vars.palette.text.main, opacity: 0.5 }}>—</Typography>)}
                                                    </Grid>
                                                    <Grid xs={2} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        {c.pm && (
                                                            <Button size="sm" variant="soft" color="primary" onClick={() => reusePagoMovil(c)} sx={{ fontFamily: "Montserrat", fontSize: "11px", borderRadius: "8px" }}>Reusar Pago Móvil</Button>
                                                        )}
                                                        {c.wire && (
                                                            <Button size="sm" variant="soft" color="success" onClick={() => reuseTransfer(c)} sx={{ fontFamily: "Montserrat", fontSize: "11px", borderRadius: "8px" }}>Reusar Transferencia</Button>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            ))}
                                            {recurrentes.length > 20 && (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
                                                    <Button size="sm" variant="soft" disabled={recPage <= 1} onClick={() => setRecPage((p) => Math.max(1, p - 1))} sx={{ fontFamily: "Montserrat", fontSize: "12px", borderRadius: "8px" }}>Anterior</Button>
                                                    <Typography sx={{ fontFamily: "Montserrat", fontSize: "12px", color: theme.vars.palette.text.main }}>
                                                        Página {recPage} de {Math.ceil(recurrentes.length / 20)} · {recurrentes.length} clientes
                                                    </Typography>
                                                    <Button size="sm" variant="soft" disabled={recPage >= Math.ceil(recurrentes.length / 20)} onClick={() => setRecPage((p) => Math.min(Math.ceil(recurrentes.length / 20), p + 1))} sx={{ fontFamily: "Montserrat", fontSize: "12px", borderRadius: "8px" }}>Siguiente</Button>
                                                </Box>
                                            )}
                                            </>
                                        ) : (
                                            <Box display="flex" justifyContent="center" py={4}>
                                                <Typography sx={{ fontFamily: "Montserrat", color: theme.vars.palette.text.main }}>No hay clientes con transacciones exitosas todavía.</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </TabPanel>
                    </Tabs>
                </Box>
        </Box>
    );
};

export default ManualCrixto;